import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ACTIVATION_TYPE } from '@lib/core/const';
import { SuggestionsDirective } from '@lib/ui';
import { ConfigurableDialog, DialogModule, DialogService } from '@proget-shared/dialog';
import { CheckboxModule } from '@proget-shared/form/checkbox';
import { CustomValidators } from '@proget-shared/form/custom-validators';
import { FormControlErrorModule } from '@proget-shared/form/form-control-error';
import { FormHelperModule } from '@proget-shared/form/helper';
import { InputModule } from '@proget-shared/form/input';
import { WysiwygComponent } from '@proget-shared/form/wysiwyg';
import { TranslateModule } from '@proget-shared/translate';
import { InputLengthCounterModule } from '@proget-shared/ui/input-length-counter';
import { TooltipModule } from '@proget-shared/ui/tooltip';
import { startWith } from 'rxjs';

import { WYSIWYG_CONFIG } from '../../const';
import { TimeToReadRange } from '../../enum';

type DeviceWithActivation = {
  activation?: string;
  activationType?: string;
};

type SendMessageConfiguration = {
  device?: DeviceWithActivation;
  items?: DeviceWithActivation[];
  initialValues?: {
    message?: string;
    audible?: boolean;
    vibrate?: boolean;
    hasDialog?: boolean;
  };
};

@Component({
  selector: 'lib-user-card-send-message',
  templateUrl: './send-message.component.html',
  styleUrls: ['./send-message.component.scss'],
  imports: [
    ReactiveFormsModule,
    DialogModule,
    WysiwygComponent,
    SuggestionsDirective,
    InputLengthCounterModule,
    FormControlErrorModule,
    CheckboxModule,
    TooltipModule,
    InputModule,
    FormHelperModule,
    TranslateModule,
  ],
})
export class SendMessageComponent implements ConfigurableDialog {
  protected readonly MAX_MESSAGE_LENGTH: number = 750;
  protected readonly wysiwygConfig = WYSIWYG_CONFIG;
  protected readonly TimeToReadRange = TimeToReadRange;
  protected readonly form: FormGroup;

  protected isDialogAllowed = true;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(DialogService);
  private readonly formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      message: [
        '',
        [
          CustomValidators.htmlContentRequired,
          Validators.maxLength(this.MAX_MESSAGE_LENGTH),
        ],
      ],
      audible: [false],
      vibrate: [false],
      hasDialog: [false],
      timeToRead: [10, [
        Validators.required,
        Validators.min(TimeToReadRange.MIN),
        Validators.max(TimeToReadRange.MAX),
        Validators.pattern(/^\d+$/),
        CustomValidators.numberRequired,
      ]],
    });

    this.form
      .get('hasDialog')
      .valueChanges.pipe(
        startWith(false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        if (!value) {
          this.form.get('timeToRead').disable();

          return;
        }

        this.form.get('timeToRead').enable();
      });
  }

  onConfiguration(configuration: SendMessageConfiguration): void {
    const device = configuration?.device
      ?? (configuration?.items?.length === 1 ? configuration.items[0] : null)
      ?? null;
    const activation = device?.activation ?? device?.activationType;

    this.isDialogAllowed = !activation
      || activation === ACTIVATION_TYPE.ANDROID_ANTI_FRAUD.KEY
      || activation === ACTIVATION_TYPE.ANDROID_FOR_WORK_DEVICE_OWNER.KEY;

    if (configuration?.initialValues) {
      this.form.patchValue(configuration.initialValues);
    }
  }

  protected close(): void {
    this.dialog.reject(this);
  }

  protected submit({ invalid, value }: FormGroup): void {
    if (invalid) {
      return;
    }

    this.dialog.resolve(this, {
      message: value.message,
      audible: value.audible,
      vibrate: value.vibrate,
      dialog: value.hasDialog ? { timeToRead: value.timeToRead } : void 0,
    });
  }
}
